import Stack from "@mui/material/Stack";
import SvgIcon from "@mui/material/SvgIcon";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import XIcon from "@untitled-ui/icons-react/build/esm/X";
import SearchMdIcon from "@untitled-ui/icons-react/build/esm/SearchMd";
import { useMemo, type FC } from "react";
import { Dialog } from "~/components/dialog";
import { Autocomplete, IconButton, InputAdornment } from "@mui/material";
import { useGetUsers } from "~/queries/useGetUsers";
import { useGetClients } from "~/queries/useGetClients";
import { FileSettingsState, SearchFilters } from "~/contexts/file-settings-context/file-settings-context";
import { useGetAgents } from "~/queries/useGetAgents";
import { useGetFileCustomFields } from "~/queries/useGetCustomFields";
import { CUSTOM_FIELD_TYPE } from "~/types/__generated/gql/graphql";
import { useGetCurrentCompanyCache } from "~/queries/useGetCurrentCompany";
interface SearchDialogProps {
  search: FileSettingsState["fileSearch"];
  updateSearch: (add: SearchFilters) => void;
  clearSearch: () => void;
  onClose: () => void;
}
const fileSearchStatuses = [{
  _id: "paid",
  label: "Is Paid"
}, {
  _id: "partial",
  label: "Is Partially Paid"
}, {
  _id: "unpaid",
  label: "Is Unpaid"
}];
export const SearchDialog: FC<SearchDialogProps> = props => {
  const {
    search,
    updateSearch,
    onClose
  } = props;
  const {
    data: company
  } = useGetCurrentCompanyCache();
  const {
    data: assignees = []
  } = useGetUsers();
  const {
    data: agents = []
  } = useGetAgents();
  const {
    data: clients = []
  } = useGetClients();
  const {
    data: customFields = []
  } = useGetFileCustomFields();
  const fileOrderStatuses = useMemo(() => {
    const orderStatus = customFields.find(f => f.name.toLowerCase() === "order status" && (f.type === CUSTOM_FIELD_TYPE.SELECT_MULTI || f.type === CUSTOM_FIELD_TYPE.SELECT_SINGLE));
    if (!orderStatus) return [];
    return orderStatus.selectOptions ? orderStatus.selectOptions.map(o => ({
      _id: o._id,
      label: o.name
    })) : [];
  }, [customFields]);
  return <Dialog dialogProps={{
    maxWidth: "md"
  }} title={<Stack alignItems="center" direction="row" justifyContent="space-between" spacing={3}>
					<Typography variant="h6">Search</Typography>
					<IconButton color="inherit" onClick={onClose}>
						<SvgIcon>
							<XIcon />
						</SvgIcon>
					</IconButton>
				</Stack>} content={<>
					<Stack sx={{
      mt: 3
    }} gap={1}>
						<TextField label={`Filter by ${company?.fileAlias?.singular ? company?.fileAlias?.singular : "Files"} #`} InputProps={{
        startAdornment: <InputAdornment position="start">
										<SvgIcon>
											<SearchMdIcon />
										</SvgIcon>
									</InputAdornment>
      }} placeholder="Search ex. 001,002..." value={search?.files?.join(",") ?? ""} onChange={e => {
        const value = e.target.value ?? "";
        const update = {
          field: "files",
          search: value.trim().split(",")
        };
        void updateSearch(update);
      }} />
						<TextField label={`Filter by Invoices #`} InputProps={{
        startAdornment: <InputAdornment position="start">
										<SvgIcon>
											<SearchMdIcon />
										</SvgIcon>
									</InputAdornment>
      }} placeholder="Search ex. 001,002..." value={search?.invoices?.join(",") ?? ""} onChange={e => {
        const value = e.target.value ?? "";
        const update = {
          field: "invoices",
          search: value.trim().split(",")
        };
        void updateSearch(update);
      }} />
						<Autocomplete fullWidth multiple disableCloseOnSelect limitTags={5} value={assignees.filter(i => search?.assignees?.includes(i._id))} options={assignees} onChange={(_, arr) => {
        const update = {
          field: "assignees",
          search: arr.map(a => a._id)
        };
        updateSearch(update);
      }} isOptionEqualToValue={(opt, val) => opt._id === val._id} getOptionLabel={option => `${option.firstName} ${option.lastName}`} renderInput={params => <TextField {...params} label="Filter by assignee" />} />
						<Autocomplete fullWidth multiple disableCloseOnSelect limitTags={5} value={agents.filter(i => search?.agents?.includes(i._id))} options={agents} onChange={(_, arr) => {
        const update = {
          field: "agents",
          search: arr.map(a => a._id)
        };
        updateSearch(update);
      }} isOptionEqualToValue={(opt, val) => opt._id === val._id} getOptionLabel={option => option.name} renderInput={params => <TextField {...params} label="Filter by agents" />} />
						<Autocomplete fullWidth multiple disableCloseOnSelect limitTags={5} value={clients.filter(i => search?.clients?.includes(i._id))} options={clients} onChange={(_, arr) => {
        const update = {
          field: "clients",
          search: arr.map(a => a._id)
        };
        updateSearch(update);
      }} isOptionEqualToValue={(opt, val) => opt._id === val._id} getOptionLabel={option => option.name} renderInput={params => <TextField {...params} label="Filter by clients" />} />
						<Autocomplete fullWidth multiple disableCloseOnSelect limitTags={5} value={assignees.filter(i => search?.creators?.includes(i._id))} options={assignees} onChange={(_, arr) => {
        const update = {
          field: "creators",
          search: arr.map(a => a._id)
        };
        updateSearch(update);
      }} isOptionEqualToValue={(opt, val) => opt._id === val._id} getOptionLabel={option => `${option.firstName} ${option.lastName}`} renderInput={params => <TextField {...params} label="Filter by creators" />} />
						{fileOrderStatuses && fileOrderStatuses.length > 0 && <Autocomplete fullWidth multiple disableCloseOnSelect limitTags={5} value={fileOrderStatuses.filter(i => search?.orderStatuses?.includes(i._id))} options={fileOrderStatuses} onChange={(_, arr) => {
        const update = {
          field: "orderStatuses",
          search: arr.map(a => a._id)
        };
        updateSearch(update);
      }} isOptionEqualToValue={(opt, val) => opt._id === val._id} getOptionLabel={option => option.label} renderInput={params => <TextField {...params} label="Filter by order status" />} />}
						<Autocomplete fullWidth multiple disableCloseOnSelect limitTags={5} value={fileSearchStatuses.filter(i => search?.paymentStatuses?.includes(i._id))} options={fileSearchStatuses} onChange={(_, arr) => {
        const update = {
          field: "paymentStatuses",
          search: arr.map(a => a._id)
        };
        updateSearch(update);
      }} isOptionEqualToValue={(opt, val) => opt._id === val._id} getOptionLabel={option => option.label} renderInput={params => <TextField {...params} label="Filter by payment status" />} />
					</Stack>
				</>} data-sentry-element="Dialog" data-sentry-component="SearchDialog" data-sentry-source-file="search-dialog.tsx" />;
};